import React from 'react';
import { ListChecks, Globe2, Shield, Sparkles, Calculator, Users, Camera, MessageSquare } from 'lucide-react';

const features = [
  {
    icon: ListChecks,
    title: "Checklists intelligentes",
    description: "Des listes de t\u00e2ches personnalis\u00e9es pour chaque pays, mises \u00e0 jour r\u00e9guli\u00e8rement.",
    lightBg: 'bg-teal-50 dark:bg-teal-900/20',
    iconColor: 'text-teal-500',
  },
  {
    icon: Calculator,
    title: "Simulateur de budget",
    description: "Estimez votre budget PVT avec des donn\u00e9es r\u00e9elles et un mode nomade.",
    lightBg: 'bg-blue-50 dark:bg-blue-900/20',
    iconColor: 'text-blue-500',
  },
  {
    icon: Users,
    title: "PVTmates",
    description: "Trouvez votre compagnon de voyage en fonction de vos dates et destinations.",
    lightBg: 'bg-rose-50 dark:bg-rose-900/20',
    iconColor: 'text-rose-500',
  },
  {
    icon: Camera,
    title: "M\u00e9dias partag\u00e9s",
    description: "Partagez vos plus beaux moments et d\u00e9couvrez ceux des autres pvtistes.",
    lightBg: 'bg-amber-50 dark:bg-amber-900/20',
    iconColor: 'text-amber-500',
  },
  {
    icon: Globe2,
    title: "Multi-destinations",
    description: "Couvre tous les pays proposant des PVT aux Fran\u00e7ais.",
    lightBg: 'bg-emerald-50 dark:bg-emerald-900/20',
    iconColor: 'text-emerald-500',
  },
  {
    icon: Shield,
    title: "Informations fiables",
    description: "Contenu v\u00e9rifi\u00e9 et mis \u00e0 jour par l'\u00e9quipe pvtistes.net.",
    lightBg: 'bg-cyan-50 dark:bg-cyan-900/20',
    iconColor: 'text-cyan-500',
  },
  {
    icon: MessageSquare,
    title: "Centre de messages",
    description: "Posez vos questions et b\u00e9n\u00e9ficiez de l'exp\u00e9rience de la communaut\u00e9.",
    lightBg: 'bg-sky-50 dark:bg-sky-900/20',
    iconColor: 'text-sky-500',
  },
  {
    icon: Sparkles,
    title: "Interface intuitive",
    description: "Une exp\u00e9rience utilisateur fluide et agr\u00e9able sur tous vos appareils.",
    lightBg: 'bg-orange-50 dark:bg-orange-900/20',
    iconColor: 'text-orange-500',
  },
];

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [isInView, setIsInView] = React.useState(false);
  React.useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.1 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return isInView;
}

export function FeaturesSection() {
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef);

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-accent-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-50 dark:bg-accent-900/20 text-accent-600 dark:text-accent-400 text-sm font-medium mb-4">
            Fonctionnalit&eacute;s
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Tout pour pr&eacute;parer votre PVT
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Des outils complets et gratuits pour organiser votre aventure sans stress.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group relative bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 border border-gray-100 dark:border-gray-700/50 hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${
                isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 75}ms` }}
            >
              <div className={`w-11 h-11 sm:w-12 sm:h-12 ${feature.lightBg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${feature.iconColor}`} />
              </div>
              <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-1.5">
                {feature.title}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
